import React from "react";
import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import Meta from "../../components/Meta";
import FormContainer from "../../components/FormContainer";
import { toast } from "react-toastify";
import {
  useUpdateProductMutation,
  useGetProductDetailsQuery,
  useUploadProductImageMutation,
} from "../../slices/productsApiSlice";

const ProductEditScreen = () => {
  const { id: productId } = useParams();

  const [name, setName] = useState("");
  const [price, setPrice] = useState(0);
  const [image, setImage] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [countInStock, setCountInStock] = useState(0);
  const [description, setDescription] = useState("");
  const [lugoPart, setLugoPart] = useState("");
  const [vendor, setVendor] = useState("");
  const [partType, setPartType] = useState("");
  const [year, setYear] = useState("");
  const [shippingPrice, setShippingPrice] = useState(0);

  const {
    data: product,
    isLoading,
    refetch,
    error,
  } = useGetProductDetailsQuery(productId);

  const [updateProduct, { isLoading: loadingUpdate }] =
    useUpdateProductMutation();

  const [uploadProductImage, { isLoading: loadingUpload }] =
    useUploadProductImageMutation();

  const navigate = useNavigate();

  useEffect(() => {
    if (product) {
      setName(product.name);
      setPrice(product.price);
      setImage(product.image);
      setBrand(product.brand);
      setCategory(product.category);
      setLugoPart(product.lugoPart);
      setVendor(product.vendor);
      setPartType(product.partType);
      setCountInStock(product.countInStock);
      setDescription(product.description);
      setYear(product.year.join(", "));
      setShippingPrice(product.shippingPrice);
    }
  }, [product]);

  const submitHandler = async (e) => {
    e.preventDefault();
    const yearArray = year.split(",").map((item) => item.trim());

    const updatedProduct = {
      productId,
      name,
      price,
      image,
      brand,
      category,
      lugoPart,
      vendor,
      partType,
      countInStock,
      description,
      year: yearArray,
      shippingPrice,
    };

    const result = await updateProduct(updatedProduct);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success("Product updated");
      refetch();
      navigate("/admin/productlist");
    }
  };

  const uploadFileHandler = async (e) => {
    const formData = new FormData();
    formData.append("image", e.target.files[0]);
    try {
      const res = await uploadProductImage(formData).unwrap();
      toast.success(res.message);
      setImage(res.image);
    } catch (err) {
      if (err.status === 413) {
        toast.error("File size is too large. Please upload a smaller image.");
      } else {
        toast.error(
          err?.data?.message || err.error || "An error occurred during upload."
        );
      }
    }
  };

  return (
    <>
      <Meta
        title="Edit Product | Admin | Lugo Parts & Restoration"
        description="Admin panel for editing product details at Lugo Parts & Restoration."
      >
        <meta name="robots" content="noindex, nofollow" />
      </Meta>

      <Link to="/admin/productlist" className="btn btn-light my-3">
        Go Back
      </Link>
      <FormContainer>
        <h1>Edit Product</h1>
        {loadingUpdate && <Loader />}

        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">{error.data.message}</Message>
        ) : (
          <Form onSubmit={submitHandler}>
            <Form.Group controlId="name" className="my-2">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="price" className="my-2">
              <Form.Label>Price</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="shippingPrice" className="my-2">
              <Form.Label>Shipping Price</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter shipping price"
                value={shippingPrice}
                onChange={(e) => setShippingPrice(Number(e.target.value))}
              ></Form.Control>
              <Form.Text className="text-muted">
                Default is $25 if not set.
              </Form.Text>
            </Form.Group>

            <Form.Group controlId="image" className="my-2">
              <Form.Label>Image</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter image url"
                value={image}
                onChange={(e) => setImage}
              ></Form.Control>
              <Form.Control
                type="file"
                label="Choose file"
                onChange={uploadFileHandler}
              ></Form.Control>
              {loadingUpload && <Loader />}
            </Form.Group>

            <Form.Group controlId="brand" className="my-2">
              <Form.Label>Brand</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="countInStock" className="my-2">
              <Form.Label>Count In Stock</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter countInStock"
                value={countInStock}
                onChange={(e) => setCountInStock(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="category" className="my-2">
              <Form.Label>Vendor Part #</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Vendor Part #"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="lugoPart" className="my-2">
              <Form.Label>Lugo Part #</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Lugo Part #"
                value={lugoPart}
                onChange={(e) => setLugoPart(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="vendor" className="my-2">
              <Form.Label>Vendor</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Vendor"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="partType" className="my-2">
              <Form.Label>Part Category</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Part Category"
                value={partType}
                onChange={(e) => setPartType(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="year" className="my-2">
              <Form.Label>Year</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter years separated by commas (e.g., 1955, 1956)"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="description" className="my-2">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Button type="submit" variant="primary" className="my-2">
              Update
            </Button>
          </Form>
        )}
      </FormContainer>
    </>
  );
};

export default ProductEditScreen;
